import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "onClose", "titleText", "type", "use", "closeable", "floating"];
import { filterProps } from '../../../utils/filterProps';
import { CloseButton } from '../CloseButton';
import styled from 'foundations-theming/utils';
import { useTypeCssObject } from './Alert.hooks';
import { StyledClickable } from '../Clickable/Clickable';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { useFireAlarmContext } from '../FireAlarm/FireAlarmProvider';
import { deepMerge } from '../../utils/deepMerge/deepMerge';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const OuterWrapper = styled.div.withConfig({
  displayName: "Alert__OuterWrapper",
  componentId: "ydl40q-0"
})(({
  $cssStyles,
  $inline,
  $typeStyles,
  $floating,
  $styleOverrides,
  theme
}) => {
  return deepMerge(Object.assign({
    /**
     * ///////////
     * Base Styles
     * ///////////
     */
    textAlign: 'left',
    position: 'relative',
    padding: `${theme.space['400']} ${theme.space['500']}`,
    minHeight: '60px',
    // no token available,
    borderWidth: theme.borderWidth['100'],
    borderRadius: theme.borderRadius['transitional-component'],
    borderStyle: 'solid',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: theme.typography['body-100'].fontSize,
    // arbitrary typography chosen

    /**
     * ///////////
     * CloseButton Styles
     * ///////////
     */
    [`& > ${StyledClickable}`]: {
      top: $inline ? theme.space['400'] : theme.space['600']
    }
  }, $inline && {
    minHeight: '44px' // no token available
  }, $typeStyles, $floating && {
    display: 'inline-flex',
    boxShadow: theme.boxShadow['200'],
    maxWidth: '668px',
    wordBreak: 'break-word',
    '@media only screen and (max-width: 768px)': {
      maxWidth: 'calc(100% - 50px)'
    }
  }, $cssStyles), $styleOverrides);
});
const TitleChildrenWrapper = styled.div.withConfig({
  displayName: "Alert__TitleChildrenWrapper",
  componentId: "ydl40q-1"
})(({
  $dismissable,
  theme
}) => {
  return Object.assign({
    /**
     * ///////////
     * Base Styles
     * ///////////
     */
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    width: '100%',
    paddingInlineEnd: theme.space['600']
  }, !$dismissable && {
    paddingInlineEnd: theme.space['0']
  });
});
export const StyledAlertTitle = styled.h5.withConfig({
  displayName: "Alert__StyledAlertTitle",
  componentId: "ydl40q-2"
})(({
  theme
}) => {
  return {
    lineHeight: theme.typography['heading-100'].lineHeight,
    fontWeight: theme.typography['heading-100'].fontWeight,
    marginTop: theme.space['0'],
    marginBottom: theme.space['100'],
    marginRight: theme.space['500'],
    fontSize: theme.typography['heading-100'].fontSize,
    letterSpacing: theme.typography['heading-100'].letterSpacing
  };
});
const ChildrenWrapper = styled.div.withConfig({
  displayName: "Alert__ChildrenWrapper",
  componentId: "ydl40q-3"
})(({
  $dismissable,
  $hasTitle,
  theme
}) => {
  return Object.assign({
    /**
     * ///////////
     * Base Styles
     * ///////////
     */
    marginTop: $hasTitle ? theme.space['100'] : theme.space['0'],
    lineHeight: theme.typography['body-100'].lineHeight,
    letterSpacing: theme.typography['body-100'].letterSpacing,
    paddingInlineEnd: theme.space['600'],
    // replaces the `has--vertical-spacing` className styling
    '& > :last-child': {
      marginBottom: theme.space['0']
    }
  }, !$dismissable && {
    paddingInlineEnd: theme.space['0']
  });
});
export const Alert = _ref => {
  var _fireAlarmContext$ale;
  let {
      children,
      onClose,
      titleText,
      type = 'info',
      use = 'default',
      closeable = false,
      floating = false
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const fireAlarmContext = useFireAlarmContext();
  const typeStyles = useTypeCssObject(type);

  // Enable backwards compatibility with selenium helpers
  // @see https://git.hubteam.com/HubSpot/hs-selenium/blob/dd3f36670180d811a35ccbca53b6841751ec1ee7/selenium-js-utils/src/UIComponents/alerts.ts#L75-L77
  const className = closeable ? 'alert-dismissable' : '';
  return /*#__PURE__*/_jsxs(OuterWrapper, Object.assign({
    $cssStyles: cssStyles,
    $inline: use === 'inline',
    $typeStyles: typeStyles,
    $floating: floating,
    "aria-atomic": true,
    "aria-live": "polite",
    $styleOverrides: (_fireAlarmContext$ale = fireAlarmContext === null || fireAlarmContext === void 0 ? void 0 : fireAlarmContext.alertStyleOverrides) !== null && _fireAlarmContext$ale !== void 0 ? _fireAlarmContext$ale : {},
    className: className,
    "data-alert-type": type,
    role: "alert"
  }, domProps, {
    children: [/*#__PURE__*/_jsxs(TitleChildrenWrapper, {
      $dismissable: closeable,
      children: [titleText && /*#__PURE__*/_jsx(StyledAlertTitle, {
        children: titleText
      }), /*#__PURE__*/_jsx(ChildrenWrapper, {
        $dismissable: closeable,
        $hasTitle: titleText !== undefined,
        children: children
      })]
    }), closeable && /*#__PURE__*/_jsx(CloseButton, {
      role: "button",
      onClick: onClose,
      size: "md",
      color: type === 'banner' ? 'icon-alert-on-fill-default' : 'icon-core-subtle'
    })]
  }));
};